/**
 * Admin System Health - Improved with tabbed layout and live exchange rate
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Activity, Users, ShoppingBag, MessageSquare, AlertTriangle,
  Clock, Database, RefreshCw, CheckCircle2, XCircle,
  DollarSign, TrendingUp, Server, Zap, Settings, Shield
} from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';

const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue', trend }) => (
  <div className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
    <div className="flex items-start justify-between">
      <div className={`p-2 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-5 h-5 text-${color}-500`} />
      </div>
      {trend && (
        <span className={`text-xs font-medium ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
    </div>
  </div>
);

const PendingCard = ({ icon: Icon, label, count, color = 'amber' }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg bg-${color}-500/10 border border-${color}-500/20`}>
    <Icon className={`w-5 h-5 text-${color}-500`} />
    <div className="flex-1">
      <p className="text-sm font-medium">{label}</p>
    </div>
    <span className={`text-lg font-bold text-${color}-500`}>{count}</span>
  </div>
);

const AdminSystemHealth = () => {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchHealth = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/admin/system-health`);
      setHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch health:', error);
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHealth();
  };

  const refreshExchangeRate = async () => {
    try {
      await axios.post(`${API}/admin/exchange-rate/refresh`);
      toast.success('Exchange rate refreshed');
      fetchHealth();
    } catch (error) {
      toast.error('Failed to refresh exchange rate');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-28 bg-secondary/50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'pending', label: 'Pending Actions', icon: Clock },
    { id: 'exchange', label: 'Exchange Rate', icon: DollarSign },
  ];

  return (
    <div className="space-y-6" data-testid="admin-system-health">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">System Health</h2>
          <p className="text-sm text-muted-foreground">
            Last updated: {health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
            health?.status === 'healthy' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
          }`}>
            {health?.status === 'healthy' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {health?.status === 'healthy' ? 'All Systems Operational' : 'Issues Detected'}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-secondary/50 rounded-lg w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Last Hour Stats */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Last Hour Activity
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={ShoppingBag}
                label="New Listings"
                value={health?.last_hour?.new_listings || 0}
                color="blue"
              />
              <StatCard
                icon={Users}
                label="New Users"
                value={health?.last_hour?.new_users || 0}
                color="green"
              />
              <StatCard
                icon={MessageSquare}
                label="New Messages"
                value={health?.last_hour?.new_messages || 0}
                color="purple"
              />
              <StatCard
                icon={AlertTriangle}
                label="Flagged Images (24h)"
                value={health?.flagged_images_24h || 0}
                color="amber"
              />
            </div>
          </div>

          {/* Quick Pending Actions */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" /> Requires Attention
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <PendingCard icon={Shield} label="Pending Reviews" count={health?.pending?.reviews || 0} />
              <PendingCard icon={Users} label="Business Verifications" count={health?.pending?.business_verifications || 0} color="blue" />
              <PendingCard icon={AlertTriangle} label="Pending Reports" count={health?.pending?.reports || 0} color="red" />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'database' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Database className="w-4 h-4" /> Database Statistics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={ShoppingBag}
                label="Total Listings"
                value={health?.totals?.listings?.toLocaleString() || 0}
                subValue={`${health?.totals?.active_listings?.toLocaleString() || 0} active`}
                color="blue"
              />
              <StatCard
                icon={Users}
                label="Total Users"
                value={health?.totals?.users?.toLocaleString() || 0}
                color="green"
              />
              <StatCard
                icon={MessageSquare}
                label="Total Messages"
                value={health?.totals?.messages?.toLocaleString() || 0}
                color="purple"
              />
              <StatCard
                icon={Server}
                label="System Status"
                value={health?.status === 'healthy' ? 'Healthy' : 'Issues'}
                color={health?.status === 'healthy' ? 'green' : 'red'}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Pending Actions
          </h3>
          <div className="grid gap-3">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <Shield className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="font-medium">Review Verifications</p>
                    <p className="text-sm text-muted-foreground">Reviews waiting for admin verification</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-amber-500">{health?.pending?.reviews || 0}</span>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">Business Verifications</p>
                    <p className="text-sm text-muted-foreground">Business accounts awaiting verification</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-blue-500">{health?.pending?.business_verifications || 0}</span>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="font-medium">Reported Content</p>
                    <p className="text-sm text-muted-foreground">Reports requiring review</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-red-500">{health?.pending?.reports || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'exchange' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-green-500/10">
                  <DollarSign className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Exchange Rate</h3>
                  <p className="text-sm text-muted-foreground">USD to XCG (Caribbean Guilder)</p>
                </div>
              </div>
              <button
                onClick={refreshExchangeRate}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Rate
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-secondary/50 rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">1 USD equals</p>
                <p className="text-3xl font-bold text-green-500">
                  {health?.exchange_rate?.usd_to_ang?.toFixed(4) || '1.79'} XCG
                </p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">1 XCG equals</p>
                <p className="text-3xl font-bold text-blue-500">
                  ${health?.exchange_rate?.xcg_to_usd?.toFixed(4) || '0.56'} USD
                </p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last Updated</span>
                <span>{health?.exchange_rate?.last_updated ? new Date(health.exchange_rate.last_updated).toLocaleString() : 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-muted-foreground">Source</span>
                <span className="capitalize">{health?.exchange_rate?.source || 'cached'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSystemHealth;
